import { AIBadgeScore, Card, Text, Icon, Divider, Skeleton } from "components/common"
import { useGetClientWhatsappInsightsQuery } from "features/clients/detail/api"
import { Flex } from "antd"
import styles from "../../index.module.less"
import BestTimeCard from "../BestTimeCard/BestTimeCard"
import { t } from "i18next"
import theme from "utils/themeVars"
import SummaryProgressBar from "./summaryProgressBar"
import CollapsibleText from "components/common/collapsibleText"
import ProjectTags from "./projectTags"
import ConversationHistorySection from "./conversationHistory/conversationHistorySection"
import EngagementInsightsPanel from "./engagementInsights/engagementnsights"

const WhatsappChatAnalysis = ({ client }) => {
  const {
    data: insightsData,
    isFetching: isFetchingInsights,
    isLoading: isLoadingInsights,
  } = useGetClientWhatsappInsightsQuery({ clientId: client?.id }, { skip: !client?.id })

  const totalConversations = insightsData?.totalConversations || 0
  const isLoading = isLoadingInsights

  if (isLoading) {
    return (
      <Card className={styles.analysisCardRoot} bordered>
        <div className={styles.analysisHeaderFixed}>
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
        <div className={styles.analysisScrollSection}>
          <Flex vertical gap={8}>
            <Skeleton active paragraph={{ rows: 4 }} />
            <Skeleton active paragraph={{ rows: 4 }} />
          </Flex>
        </div>
      </Card>
    )
  }

  return (
    <Card className={styles.analysisCardRoot} bordered>
      <div className={styles.analysisHeaderFixed}>
        <Card>
          <Flex justify="space-between" align="center" gap={8} style={{ width: "100%" }}>
            <Text className="bold" style={{ fontSize: 18 }}>
              {t("WhatsApp Conversation Analysis")}
            </Text>
            <AIBadgeScore
              totalScore={insightsData?.overallEngagementScore}
              title={t("AI Analysis Score:")}
              buttonProps={{
                style: { height: 26 },
                size: "small",
                shape: "round",
              }}
            />
          </Flex>
        </Card>

        <BestTimeCard
          bestTime={insightsData?.bestMessageHour || t("No best messaging hour yet")}
          title={
            insightsData?.bestMessageHour && insightsData?.isCurrentHourBest
              ? t("Message Now!")
              : t("Best Time to Message:")
          }
          description={
            insightsData?.bestMessageHour
              ? insightsData?.isCurrentHourBest
                ? t("Excellent opportunity! Client is the most responsive at this time. Message now")
                : t(
                    "AI predicts client is most likely to reply to messages during this window. Plan your follow-up accordingly."
                  )
              : t("Once enough timed message history exists, a recommended hour range will appear here.")
          }
          loading={isFetchingInsights}
        />
      </div>

      <div className={styles.analysisScrollSection}>
        <Flex vertical gap={8}>
          <Card>
            <Flex justify="space-between">
              <Flex gap="12px" align="flex-start">
                <Icon
                  icon="BiComment"
                  hasBackground={true}
                  color={theme["accent-color"]}
                  size={20}
                  iconContainerStyle={{
                    backgroundColor: "#e6f5f8",
                    width: 50,
                    height: 50,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "8px",
                  }}
                />
                <Flex vertical gap="4px">
                  <Text className="fs14" type="secondary">
                    {t("Total Conversations")}
                  </Text>
                  <Text className="bold fs14">{totalConversations}</Text>
                </Flex>
              </Flex>
            </Flex>

            <Divider />

            <Flex align="center" justify="space-between" style={{ width: "100%" }}>
              <Flex vertical gap="4px" align="flex-start" style={{ flex: 1 }}>
                <Text className="fs14" type="secondary">
                  {t("Qualified Conversations")}
                </Text>
                <Text className="bold fs14">
                  {insightsData?.qualifiedConversations || 0}/{totalConversations}
                </Text>
              </Flex>

              <Divider type="vertical" style={{ height: "40px", margin: "0 40px 0 2px" }} />

              <Flex vertical gap="4px" align="flex-start" style={{ flex: 1 }}>
                <Text className="fs14" type="secondary">
                  {t("Relevant Conversations")}
                </Text>
                <Text className="bold fs14">
                  {insightsData?.relevantConversations || 0}/{insightsData?.qualifiedConversations || 0}
                </Text>
              </Flex>

              <Divider type="vertical" style={{ height: "40px", margin: "0 40px 0 2px" }} />

              <Flex vertical gap="4px" align="flex-start" style={{ flex: 1 }}>
                <Text className="fs14" type="secondary">
                  {t("High Engagement")}
                </Text>
                <Text className="bold fs14">
                  {insightsData?.highEngagementConversations || 0}/{insightsData?.relevantConversations || 0}
                </Text>
              </Flex>
            </Flex>
          </Card>

          <Card>
            <Flex vertical gap={16}>
              <Flex vertical gap={8}>
                <Flex align="center" gap={8}>
                  <Icon icon="AiOutlineBulb" color={theme["accent-color"]} size={20} />
                  <Text className="bold fs16">{t("Conversation Summary")}</Text>
                </Flex>

                <CollapsibleText
                  text={insightsData?.overallSummary || t("No summary available")}
                  collapsibleLength={250}
                  className="gray-800 fs14"
                  buttonColor={theme["accent-color"]}
                />
              </Flex>
            </Flex>

            <Text italic className="fs16 mbe-8">{t("Discussion Themes:")}</Text>

            <SummaryProgressBar data={insightsData?.overallThemes} loading={isFetchingInsights} />

            <Divider />

            <Text italic className="fs16 gray-900 mbe-8" block={true}>
              {t("Projects Discussed:")}
            </Text>

            <ProjectTags projects={insightsData?.overallProjects} loading={isFetchingInsights} />
            <Divider />

            <Flex vertical gap={8}>
              <Flex align="center" gap={8}>
                <Icon icon="BsClipboardCheck" color={theme["accent-color"]} size={18} />
                <Text className="bold fs16 ">{t("Next Proposed Steps")}</Text>
              </Flex>

              <ul className="gray-800 fs14">
                {(insightsData?.nextSteps ?? []).map(step => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            </Flex>
          </Card>

          <Card>
            <ConversationHistorySection
              key={client?.id}
              clientId={client?.id}
              scoresBreakdown={insightsData?.scoresBreakdown}
            />
          </Card>

          <Card>
            <EngagementInsightsPanel key={client?.id} clientId={client?.id} />
          </Card>
        </Flex>
      </div>
    </Card>
  )
}

export default WhatsappChatAnalysis
